import { DOCUMENT } from '@angular/common';
import { Injectable, effect, inject, signal } from '@angular/core';

/** Local storage key under which the dark mode setting is persisted. */
export const DARK_MODE_STORAGE_KEY = 'darkMode';

/** CSS class set on the root element while dark mode is active. */
export const DARK_MODE_CLASS = 'app-dark';

/**
 * Manages the application theme. Reads the saved preference on creation
 * (falling back to the OS preference) and keeps the root class and local
 * storage in sync with the current state.
 */
@Injectable({ providedIn: 'root' })
export class Theme {
  private document = inject(DOCUMENT);

  /** Whether dark mode is currently active. */
  public readonly darkMode = signal<boolean>(this.loadInitialValue());

  constructor() {
    effect(() => {
      const enabled = this.darkMode();
      this.document.documentElement.classList.toggle(DARK_MODE_CLASS, enabled);
      this.document.documentElement.style.colorScheme = enabled
        ? 'dark'
        : 'light';
      this.save(enabled);
    });
  }

  /** Sets dark mode explicitly. */
  public setDarkMode(enabled: boolean): void {
    this.darkMode.set(enabled);
  }

  /** Toggles between light and dark mode. */
  public toggle(): void {
    this.darkMode.update(value => !value);
  }

  private loadInitialValue(): boolean {
    try {
      const stored = localStorage.getItem(DARK_MODE_STORAGE_KEY);
      if (stored !== null) {
        return JSON.parse(stored) === true;
      }
    } catch {
      // Storage unavailable (SSR/private mode) or invalid value: use default.
    }
    return (
      typeof matchMedia === 'function' &&
      matchMedia('(prefers-color-scheme: dark)').matches
    );
  }

  private save(enabled: boolean): void {
    try {
      localStorage.setItem(DARK_MODE_STORAGE_KEY, JSON.stringify(enabled));
    } catch {
      // Ignore storage errors.
    }
  }
}
